import React from 'react';
import './Privacy.css';

const LAST_UPDATED = 'March 18, 2026';
const CONTACT_EMAIL = 'info@the-patch-guys.com';
const APP_NAME = 'The Patch Guys';
const WEBSITE = 'https://the-patch-guys.com';

export default function Privacy() {
  return (
    <div className="privacy">
      <section className="privacy__hero">
        <div className="container">
          <div className="badge badge-blue" style={{marginBottom:'20px'}}>Legal</div>
          <h1 className="section-title" style={{color:'white', marginBottom:'12px'}}>Privacy Policy</h1>
          <p style={{color:'rgba(255,255,255,0.55)', fontSize:'15px'}}>
            Last updated: {LAST_UPDATED}
          </p>
        </div>
      </section>

      <section className="section privacy__content">
        <div className="container privacy__body">

          <div className="privacy__intro">
            <p>
              Welcome to {APP_NAME}. We are committed to protecting your privacy and
              the privacy of your children. This Privacy Policy explains how we collect,
              use, and safeguard information when you use our mobile application and website.
            </p>
            <p>
              By using {APP_NAME}, you agree to the collection and use of information
              in accordance with this policy. If you do not agree with this policy,
              please do not use our app or website.
            </p>
          </div>

          <div className="privacy__section">
            <h2>1. Who We Are</h2>
            <p>
              {APP_NAME} is a youth sports patch tracking platform that allows players
              and their parents to collect, track, and display patches from teams and events
              they have competed against or attended. Our service is operated for use by
              families participating in youth sports.
            </p>
            <p>
              For questions about this policy, contact us at:{' '}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </p>
          </div>

          <div className="privacy__section">
            <h2>2. Information We Collect</h2>

            <h3>Account Information</h3>
            <p>When you create an account, we collect:</p>
            <ul>
              <li>Email address (used for passwordless login via one-time code)</li>
              <li>First and last name</li>
              <li>City and state of residence</li>
              <li>Account type (parent or athlete)</li>
            </ul>

            <h3>Child Profile Information</h3>
            <p>
              Parents can create profiles for their children within their account. We collect:
            </p>
            <ul>
              <li>Child's first name (display name only)</li>
            </ul>
            <p>
              Children do not create their own accounts. All child profiles are created
              and managed by a parent or guardian. We do not knowingly collect personal
              information directly from children under 13.
            </p>

            <h3>Usage Data</h3>
            <p>When using the app, we collect:</p>
            <ul>
              <li>Teams scanned (team name, location, sport)</li>
              <li>Events attended (event name, location, dates)</li>
              <li>Game scores and dates (optional, entered by the user)</li>
              <li>Trophy milestones earned</li>
            </ul>

            <h3>Team and Event Registration</h3>
            <p>When a team or event organizer registers, we collect:</p>
            <ul>
              <li>Team or event name, city, state, and sport</li>
              <li>Contact name and email address</li>
              <li>Team or event logo image</li>
              <li>Website and social media links (optional)</li>
            </ul>

            <h3>Device and Technical Information</h3>
            <p>We may automatically collect:</p>
            <ul>
              <li>Device type and operating system (iOS or Android)</li>
              <li>App version</li>
              <li>Crash reports and error logs (for improving the app)</li>
            </ul>
          </div>

          <div className="privacy__section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide and maintain the {APP_NAME} service</li>
              <li>Authenticate users via one-time passcode (OTP) sent by email</li>
              <li>Display your personal patch map and collection</li>
              <li>Track trophy milestones and achievements</li>
              <li>Allow team and event organizers to register and receive QR codes</li>
              <li>Process and approve team and event registrations</li>
              <li>Respond to customer support inquiries</li>
              <li>Improve and optimize the app experience</li>
            </ul>
            <p>
              We do <strong>not</strong> use your information for advertising, and we do
              <strong> not</strong> sell your personal information to third parties.
            </p>
          </div>

          <div className="privacy__section">
            <h2>4. Children's Privacy (COPPA)</h2>
            <p>
              {APP_NAME} is designed for use by families participating in youth sports.
              Accounts must be created by users who are 13 years of age or older.
              Parents and guardians may create accounts to manage their children's
              patch collections.
            </p>
            <ul>
              <li>
                You must be at least 13 years old to create an account. By creating
                an account you confirm that you meet this age requirement.
              </li>
              <li>
                Parents and guardians may create accounts on behalf of children under 13.
                Child profiles within an account contain only a first name.
              </li>
              <li>
                We do not knowingly collect personal information directly from children
                under the age of 13 without parental consent. If you are under 13,
                please have a parent or guardian create the account for you.
              </li>
              <li>
                If you believe we have inadvertently collected information from a child
                under 13 without parental consent, please contact us at{' '}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we will
                promptly delete that information.
              </li>
            </ul>
          </div>

          <div className="privacy__section">
            <h2>5. Data Storage and Security</h2>
            <p>
              Your data is stored securely using Google Firebase, a cloud platform
              provided by Google LLC. Firebase provides industry-standard security
              including encryption in transit (HTTPS/TLS) and at rest.
            </p>
            <p>
              We implement reasonable technical and organizational measures to protect
              your information. However, no method of electronic transmission or storage
              is 100% secure, and we cannot guarantee absolute security.
            </p>
            <p>
              Our data is stored in the United States. By using our service, you consent
              to your data being stored and processed in the United States.
            </p>
          </div>

          <div className="privacy__section">
            <h2>6. Third-Party Services</h2>
            <p>We use the following third-party services that may process your data:</p>
            <ul>
              <li>
                <strong>Google Firebase</strong> — database, authentication, file storage,
                and cloud functions. Subject to{' '}
                <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">
                  Google's Privacy Policy
                </a>.
              </li>
              <li>
                <strong>Google Maps Platform</strong> — map display and city geocoding.
                Subject to{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                  Google's Privacy Policy
                </a>.
              </li>
              <li>
                <strong>SendGrid</strong> — email delivery for one-time passcodes.
                Subject to{' '}
                <a href="https://www.twilio.com/en-us/legal/privacy" target="_blank" rel="noopener noreferrer">
                  Twilio SendGrid's Privacy Policy
                </a>.
              </li>
            </ul>
            <p>
              We do not share your personal information with these third parties beyond
              what is necessary to provide our service.
            </p>
          </div>

          <div className="privacy__section">
            <h2>7. Location Information</h2>
            <p>
              {APP_NAME} does <strong>not</strong> access your device's GPS or precise
              location. The map in our app is populated using city and state information
              that you or team registrants enter manually — not your device's location
              services.
            </p>
          </div>

          <div className="privacy__section">
            <h2>8. Camera and Photo Library</h2>
            <p>
              The app requests access to your device's camera solely to scan QR codes
              on team and event patches. We do not store, upload, or share any camera
              images.
            </p>
            <p>
              Photo library access is requested only when uploading a team or event logo
              during registration. Uploaded logos are stored in Firebase Storage and
              displayed within the app.
            </p>
          </div>

          <div className="privacy__section">
            <h2>9. Data Retention</h2>
            <p>
              We retain your account information and patch collection data for as long
              as your account is active. If you would like to delete your account and
              all associated data, please contact us at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we will
              process your request within 30 days.
            </p>
          </div>

          <div className="privacy__section">
            <h2>10. Your Rights</h2>
            <p>You have the right to:</p>
            <ul>
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your account and associated data</li>
              <li>Withdraw consent at any time by discontinuing use of the app</li>
            </ul>
            <p>
              To exercise any of these rights, contact us at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            </p>
          </div>

          <div className="privacy__section">
            <h2>11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you
              of any significant changes by updating the "Last updated" date at the top
              of this page. We encourage you to review this policy periodically.
            </p>
            <p>
              Continued use of {APP_NAME} after changes are posted constitutes your
              acceptance of the updated policy.
            </p>
          </div>

          <div className="privacy__section">
            <h2>12. Contact Us</h2>
            <p>
              If you have any questions or concerns about this Privacy Policy or our
              data practices, please contact us:
            </p>
            <div className="privacy__contact">
              <p><strong>{APP_NAME}</strong></p>
              <p>
                Email:{' '}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              </p>
              <p>
                Website:{' '}
                <a href={WEBSITE} target="_blank" rel="noopener noreferrer">{WEBSITE}</a>
              </p>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}